import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;
const POLL_INTERVAL = 5 * 60 * 1000;

const getToken = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "null")?.Token ?? "";
  } catch {
    return "";
  }
};

const authHeader = () => ({ Authorization: `Bearer ${getToken()}` });

const fetchGet = (path) =>
  axios
    .get(API_URL + path, { headers: authHeader() })
    .then((r) => r.data?.data ?? [])
    .catch(() => []);

const fetchPost = (path, body = {}) =>
  axios
    .post(API_URL + path, body, { headers: authHeader() })
    .then((r) => r.data?.data ?? [])
    .catch(() => []);

const PARAM = { start: 0, count: 100, search: "", BisnisUnitID: "U", Menu: "Approval" };

// Mapping tiap sumber ke label + path navigasi
const mapItems = (items = [], label, getPath) =>
  items.map((item) => ({
    label,
    title: getPath(item).title,
    path: getPath(item).path,
    state: getPath(item).state ?? null,
  }));

export function useApprovalCount() {
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [
        hrdData,
        poData,
        prData,
        prOsData,
        pcData,
        gaData,
        gauData,
        geData,
        returnData,
        kendaraanData,
        cjData,
        jurnalData,
        pchoData,
        pcsoData,
        penhoData,
        pensoData,
        danaData,
        nonPoData,
        jrData,
      ] = await Promise.all([
        fetchPost("getApprovalListModel", {}),
        fetchGet("Procurement/PurchaseOrder/table/approval"),
        fetchPost("Procurement/PurchaseRequisition/table/approval", PARAM),
        fetchPost("Procurement/PurchaseRequisition/table/approvaloutstock", PARAM),
        fetchPost("Procurement/PriceComparison/table/approval"),
        fetchGet("Procurement/GoodsAcceptance/approval"),
        fetchGet("Procurement/GoodsAcceptanceUser/approval"),
        fetchGet("Procurement/GoodsExpenditure/approval"),
        fetchGet("Procurement/GoodsReturn/approval"),
        fetchGet("Procurement/Kendaraan/approval"),
        fetchGet("Accounting/CashJournal/approval"),
        fetchGet("Accounting/Approval"),
        fetchGet("Accounting/CashJournal/approvalp/U0001"),
        fetchGet("Accounting/CashJournal/approvalp/U0002"),
        fetchGet("Accounting/CashJournal/approvalPenerimaan/U0001"),
        fetchGet("Accounting/CashJournal/approvalPenerimaan/U0002"),
        fetchGet("Accounting/CashJournal/approvalDana/"),
        fetchGet("Accounting/PembayaranAP/approval"),
        fetchGet("operational/jobrequest/approval"),
      ]);

      const allItems = [
        ...mapItems(hrdData.filter((i) => i.Menu !== "JR Activity"), "HRD", (i) => ({
          title: `${i.Menu} — ${i.Pemohon ?? i.Nik ?? ""}`,
          path: "/approval-list",
          state: null,
        })),
        ...mapItems(poData, "Purchase Order", (i) => ({
          title: `PO — ${i.PurchaseOrderID ?? i.PurchaseRequisitionID ?? ""}`,
          path: "/procurment/approval/purchase-order",
          state: { PurchaseRequisitionID: i.PurchaseRequisitionID },
        })),
        ...mapItems(prData, "Purchase Requisition", (i) => ({
          title: `MSR — ${i.PurchaseRequisitionID ?? ""}`,
          path: "/procurment/approval/purchase-requisition",
          state: { PurchaseRequisitionID: i.PurchaseRequisitionID },
        })),
        ...mapItems(prOsData, "Purchase Requisition", (i) => ({
          title: `MSR — ${i.PurchaseRequisitionID ?? ""}`,
          path: "/procurment/approval/purchase-requisition",
          state: { PurchaseRequisitionID: i.PurchaseRequisitionID },
        })),
        ...mapItems(pcData, "Price Comparison", (i) => ({
          title: `PC — ${i.PRComparisonID ?? ""}`,
          path: "/procurment/approval/price-comparison",
          state: { PurchaseRequisitionID: i.PurchaseRequisitionID, PRComparisonID: i.PRComparisonID, PRCApprovalID: i.PRCApprovalID },
        })),
        ...mapItems(gaData, "Penerimaan Barang", (i) => ({
          title: `PB — ${i.GoodsAcceptanceID ?? ""}`,
          path: "/gudang/approval/penerimaan-barang",
          state: { GoodsAcceptanceID: i.GoodsAcceptanceID },
        })),
        ...mapItems(gauData, "Penerimaan Barang User", (i) => ({
          title: `PBU — ${i.GoodsAcceptanceUserID ?? ""}`,
          path: "/gudang/approval/penerimaan-barang-user",
          state: { GoodsAcceptanceUserID: i.GoodsAcceptanceUserID },
        })),
        ...mapItems(geData, "Pengeluaran Barang", (i) => ({
          title: `GE — ${i.GoodsExpenditureID ?? ""}`,
          path: "/gudang/approval/pengeluaran-barang",
          state: { GoodsExpenditureID: i.GoodsExpenditureID },
        })),
        ...mapItems(returnData, "Return Barang", (i) => ({
          title: `Return — ${i.GoodsReturnID ?? ""}`,
          path: "/gudang/approval/return-barang",
          state: { GoodsReturnID: i.GoodsReturnID },
        })),
        ...mapItems(kendaraanData, "Kendaraan", (i) => ({
          title: `Kendaraan — ${i.TKendaraanID ?? ""}`,
          path: `/equipment/kendaraan/kendaraan/${i.TKendaraanID}`,
        })),
        ...mapItems(cjData, "Pembayaran Uang Muka", (i) => ({
          title: `CJ — ${i.CashJournalID ?? ""}`,
          path: `/finance/approval/form-kas-bank/${(i.CashJournalID ?? "").replaceAll("/", "-")}`,
        })),
        ...mapItems(jurnalData, "Jurnal", (i) => ({
          title: `Jurnal — ${i.AccountingID ?? ""}`,
          path: `/accounting/approval/jurnal/${(i.AccountingID ?? "").replaceAll("/", "-")}`,
        })),
        ...mapItems(pchoData, "Pengeluaran Kas HO", (i) => ({
          title: `Kas HO — ${i.CashJournalID ?? ""}`,
          path: `/finance/approval/pattycash/${(i.CashJournalID ?? "").replaceAll("/", "-")}`,
        })),
        ...mapItems(pcsoData, "Pengeluaran Kas SO", (i) => ({
          title: `Kas SO — ${i.CashJournalID ?? ""}`,
          path: `/finance/approval/pattycash/${(i.CashJournalID ?? "").replaceAll("/", "-")}`,
        })),
        ...mapItems(penhoData, "Penerimaan Kas HO", (i) => ({
          title: `Pen HO — ${i.CashJournalID ?? ""}`,
          path: `/finance/approval/pattycash/${(i.CashJournalID ?? "").replaceAll("/", "-")}`,
        })),
        ...mapItems(pensoData, "Penerimaan Kas SO", (i) => ({
          title: `Pen SO — ${i.CashJournalID ?? ""}`,
          path: `/finance/approval/pattycash/${(i.CashJournalID ?? "").replaceAll("/", "-")}`,
        })),
        ...mapItems(danaData, "Penerimaan Dana", (i) => ({
          title: `Dana — ${i.CashJournalID ?? ""}`,
          path: `/finance/approval/pattycash/${(i.CashJournalID ?? "").replaceAll("/", "-")}`,
        })),
        ...mapItems(nonPoData, "Pembayaran AP", (i) => ({
          title: `AP — ${i.PembayaranAPID ?? ""}`,
          path: `/finance/approval/pembayaran-ap/${(i.PembayaranAPID ?? "").replaceAll("/", "-")}`,
        })),
        ...mapItems(jrData, "Job Request", (i) => ({
          title: `JR — ${i.JRID ?? ""}`,
          path: "/operasional-activity-approval",
          state: { JRID: i.JRID },
        })),
      ];

      setItems(allItems);
      setCount(allItems.length);
    } catch {
      // tetap dari state sebelumnya jika gagal
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [refresh]);

  return { count, items, loading, refresh };
}
